import { isQueuedMessage, type Message } from '@jerome2/shared';
import { createStore, type Store } from '../lib/store.ts';
import { chatStore, type ChatState } from '../state/chat.ts';
import { createNarrator, loadRate, markdownToSpeech, type Narrator } from './narrator.ts';
import { sharedAudio } from './tts.ts';

// Antwoord voorlezen in de chat: heeft Jeroen zijn bericht ingesproken (op de telefoon), dan leest Jerome zijn
// antwoord voor. arm() zet de lezer op scherp voor één gesprek en het volgnummer van dat bericht; zodra het
// gesprek weer rust (status idle) en er een tekstantwoord na dat bericht staat, gaat dat als spreektekst in
// blokken naar de voorlezer (narrator.ts) op het gedeelde audio-element (tts.ts), dat in de tik op de
// microfoon ontgrendeld is: een vers element weigert iOS buiten een tik. Stop, het verlaten van het gesprek of
// een ander gesprek stopt hem; een nieuw bericht dat niet in de wachtrij komt ook, maar dat beslist het scherm
// (ChatScreen roept stop() aan). Zolang de microfoon aanstaat (hold/release) zwijgt hij: wat klinkt stopt, en
// een wachtende lezer begint pas met lezen als de microfoon uit is, anders schrijft die Jeromes stem op. Eindigt
// Jerome met een fout (ook na een half antwoord) of zonder tekst, dan blijft het stil (het scherm toont het al).
// Een anker is het volgnummer van Jeroens bericht, of zijn id: staat dat bericht nog in de wachtrij (Jerome was
// nog met het vorige bezig), dan wacht de lezer tot het is opgepakt (nieuw volgnummer). Wacht hij in dit gesprek
// al op een eerder bericht, dan blijft dat anker staan en komt het nieuwe erbij: na afloop leest hij alles na het
// oudste anker, dus ook het antwoord op het eerdere bericht; een anker dat uit de wachtrij is gehaald vervalt, en
// zonder ankers stopt hij. Leest hij al voor, dan leest hij door en komt het anker erbij: is hij uitgelezen (of
// legt de microfoon hem stil), dan wacht hij op het antwoord daarop. Wat hij al voorlas, leest hij niet opnieuw;
// een fout of Stop laat ook die ankers vervallen.

export interface AnswerReaderState {
  /** idle: niets; armed: wacht op het antwoord; loading/speaking/paused: leest; error: mislukt, met één zin voor Jeroen. */
  status: 'idle' | 'armed' | 'loading' | 'speaking' | 'paused' | 'error';
  chatId: string | null;
  /** Het blok dat speelt of geladen wordt (vanaf 0) en het totaal; alleen tijdens het lezen. */
  index: number;
  total: number;
  error: string | null;
}

export interface AnswerReader {
  state: Store<AnswerReaderState>;
  /** Wacht op het antwoord op Jeroens bericht in dit gesprek: `after` is zijn volgnummer (-1: het eerste antwoord in een nieuw
   * gesprek) of zijn id (dan ook als het nog in de wachtrij staat). Wacht hij in dit gesprek al op een eerder bericht, dan blijft
   * dat anker staan (na dat antwoord komt ook dit) en komt dit erbij; leest hij al voor, dan leest hij door en wacht hij daarna
   * op dit antwoord. Een ander gesprek vervangt alles. */
  arm(chatId: string, after: number | string): void;
  /** Stopt het lezen én het wachten; met een gesprek alleen als de lezer bij dat gesprek hoort. */
  stop(chatId?: string | null): void;
  /** De microfoon gaat aan: wat klinkt stopt (anders schrijft de microfoon Jeromes stem op) en een wachtende lezer begint niet
   * met lezen tot release(); kwamen er tijdens het lezen ankers bij, dan wacht hij daarna op die antwoorden. */
  hold(): void;
  /** De microfoon is uit: een wachtende lezer mag weer lezen. */
  release(): void;
}

/** Korte blokken: het eerste geluid komt snel, en Stop kapt af zonder lang na te spelen. */
export const ANSWER_BLOCK_CHARS = 600;

const IDLE: AnswerReaderState = { status: 'idle', chatId: null, index: 0, total: 0, error: null };

/** De tekst van het antwoord: alle tekstberichten van Jerome na `afterSeq`, in volgorde (voor een toolaanroep staat soms al een zin). */
export function answerText(messages: readonly Message[], afterSeq: number): string {
  return messages
    .filter((m) => m.seq > afterSeq && m.role === 'assistant' && m.kind === 'text')
    .map((m) => String(m.content['text'] ?? '').trim())
    .filter(Boolean)
    .join('\n\n');
}

export interface AnswerReaderOptions {
  /** Voor tests: een eigen gesprekken-store en voorlezer. */
  chats?: Store<ChatState>;
  narrator?: Narrator;
}

export function createAnswerReader(opts: AnswerReaderOptions = {}): AnswerReader {
  const chats = opts.chats ?? chatStore;
  const state = createStore<AnswerReaderState>(IDLE);
  let narrator: Narrator | null = null;
  /** De ankers van het gesprek waarvoor hij op scherp staat; het oudste bepaalt waarna hij leest. Tijdens het lezen: de ankers die
   * er sindsdien bij kwamen (daarna wacht hij op die antwoorden). */
  let anchors: Array<number | string> = [];
  /** De microfoon staat aan: niet beginnen met lezen (hold/release). */
  let held = false;
  let unwatch: (() => void) | null = null;
  /** Terwijl wij de voorlezer zelf starten of stoppen: zijn tussenstand (even idle) is dan geen einde. */
  let busy = false;

  const stopWatching = () => {
    unwatch?.();
    unwatch = null;
  };

  const reading = (s: AnswerReaderState) => s.status === 'loading' || s.status === 'speaking' || s.status === 'paused';

  /** Op scherp voor dit gesprek met de ankers die er staan: lezen zodra het antwoord er is. */
  const watch = (chatId: string) => {
    stopWatching();
    state.set({ ...IDLE, status: 'armed', chatId });
    unwatch = chats.subscribe(check);
    check();
  };

  /** Klaar met lezen: kwamen er intussen ankers bij, dan wacht hij op die antwoorden; anders is hij klaar. */
  const finished = () => {
    const chatId = state.get().chatId;
    if (anchors.length && chatId) watch(chatId);
    else state.set(IDLE);
  };

  /** De stand van de voorlezer overnemen zolang wij lezen. */
  const follow = (n: Narrator) => {
    n.state.subscribe(() => {
      if (busy) return;
      const s = state.get();
      if (s.status === 'idle' || s.status === 'armed') return;
      const ns = n.state.get();
      if (ns.status === 'laden') state.patch({ status: 'loading', index: ns.index, total: ns.total });
      else if (ns.status === 'speelt') state.patch({ status: 'speaking', index: ns.index, total: ns.total });
      else if (ns.status === 'pauze') state.patch({ status: 'paused' });
      else if (ns.status === 'fout') {
        // Mislukt: ook wat er tijdens het lezen bij kwam, vervalt (de balk toont de fout tot Stop).
        anchors = [];
        state.patch({ status: 'error', error: ns.error });
      }
      // Het laatste blok is uitgesproken.
      else finished();
    });
    return n;
  };
  if (opts.narrator) narrator = follow(opts.narrator);

  /** De voorlezer, pas bij het eerste antwoord gemaakt: op het gedeelde element, met het tempo uit de instellingen. */
  const voice = (): Narrator => narrator ?? (narrator = follow(createNarrator({ blockChars: ANSWER_BLOCK_CHARS, rate: loadRate(), makeAudio: () => sharedAudio() ?? new Audio() })));

  /** Zet de voorlezer stil; zijn tussenstand (idle) is dan geen einde van het lezen. */
  const silence = () => {
    if (!narrator) return;
    busy = true;
    narrator.stop();
    busy = false;
  };

  const stop = (chatId?: string | null) => {
    const s = state.get();
    if (chatId !== undefined && s.chatId !== chatId) return;
    stopWatching();
    anchors = [];
    if (s.status === 'idle') return;
    state.set(IDLE);
    silence();
  };

  const read = (text: string) => {
    const speech = markdownToSpeech(text);
    if (!speech.trim()) return state.set(IDLE);
    const n = voice();
    busy = true;
    n.play(speech);
    busy = false;
    const ns = n.state.get();
    if (ns.status === 'idle') return state.set(IDLE);
    state.set({ status: ns.status === 'fout' ? 'error' : 'loading', chatId: state.get().chatId, index: ns.index, total: ns.total, error: ns.error });
  };

  /** Het volgnummer waarna het antwoord komt: het laagste van de ankers. Een getal is dat volgnummer zelf. Een id wijst Jeroens
   * bericht aan: staat het nog in de wachtrij, dan wachten (het krijgt bij het oppakken een nieuw volgnummer); is het weg (uit de
   * wachtrij gehaald), dan vervalt dat anker. 'weg': geen anker over, niets om voor te lezen. null: nog niet te zeggen (ook zolang
   * de berichten niet geladen zijn). */
  const anchorSeq = (messages: readonly Message[] | undefined): number | null | 'weg' => {
    let lowest: number | null = null;
    for (const anchor of anchors) {
      let seq: number;
      if (typeof anchor === 'number') seq = anchor;
      else {
        if (!messages) return null;
        const own = messages.find((m) => m.id === anchor);
        if (!own) continue;
        if (isQueuedMessage(own)) return null;
        seq = own.seq;
      }
      lowest = lowest === null ? seq : Math.min(lowest, seq);
    }
    return lowest ?? 'weg';
  };

  const check = () => {
    const s = state.get();
    if (s.status !== 'armed' || !s.chatId) return;
    const all = chats.get();
    const chat = all.chats[s.chatId];
    const afterSeq = anchorSeq(all.messages[s.chatId]);
    if (afterSeq === 'weg') {
      stopWatching();
      state.set(IDLE);
      return;
    }
    if (afterSeq === null || !chat || chat.status === 'running') return;
    // Jerome eindigde met een fout: stil, ook als er al een stuk antwoord stond (het scherm toont de fout).
    if (chat.status === 'error') {
      stopWatching();
      state.set(IDLE);
      return;
    }
    // De microfoon staat aan: het antwoord wacht tot die uit is.
    if (held) return;
    const messages = all.messages[s.chatId] ?? [];
    const text = answerText(messages, afterSeq);
    if (text) {
      stopWatching();
      // Alles na het oudste anker gaat mee, dus deze ankers zijn afgehandeld; wat er tijdens het lezen bij komt, wacht daarna.
      anchors = [];
      read(text);
      return;
    }
    // Rust zonder tekst: Jerome eindigde zonder iets te zeggen (alleen een toolregel of een foutbericht). Staat er nog
    // niets van hem na het bericht, dan zijn de berichten nog onderweg en wachten we.
    if (messages.some((m) => m.seq > afterSeq && m.role === 'assistant')) {
      stopWatching();
      state.set(IDLE);
    }
  };

  return {
    state,
    arm(chatId, after) {
      const s = state.get();
      if (s.chatId === chatId && (s.status === 'armed' || reading(s))) {
        // Hij wacht hier al op een eerder bericht, of leest al voor: dat blijft (dit antwoord komt daarna), dit anker komt erbij.
        anchors = [...anchors, after];
        if (s.status === 'armed') check();
        return;
      }
      stop();
      anchors = [after];
      watch(chatId);
    },
    stop,
    hold() {
      held = true;
      const s = state.get();
      // Een wachtende lezer wacht door; een fout ruimt hij op.
      if (!reading(s)) {
        if (s.status === 'error') stop();
        return;
      }
      // Wat klinkt (of laadt) stopt, en dat leest hij niet opnieuw; kwamen er tijdens het lezen ankers bij, dan wacht hij op die
      // antwoorden (tot de microfoon uit is).
      silence();
      finished();
    },
    release() {
      held = false;
      check();
    },
  };
}

/** De lezer van de app (één tegelijk: er is ook maar één gedeeld audio-element). */
export const answerReader: AnswerReader = createAnswerReader();

export function armAnswerReader(chatId: string, after: number | string): void {
  answerReader.arm(chatId, after);
}

export function stopAnswerReader(chatId?: string | null): void {
  answerReader.stop(chatId);
}

/** De microfoon gaat aan (ChatInput): Jerome zwijgt tot releaseAnswerReader(). */
export function holdAnswerReader(): void {
  answerReader.hold();
}

export function releaseAnswerReader(): void {
  answerReader.release();
}
